import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, Bot, Circle } from "lucide-react";
import { cn } from "@/lib/utils";
import { agentRoleLabel } from "@/lib/agent-display";
import { useTenant } from "@/context/tenant-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { AgentChatAvatar } from "@/components/chat/agent-chat-avatar";

interface MobileAgentSheetProps {
  open: boolean;
  onClose: () => void;
  selectedAgentId: string | null;
  onSelect: (agentId: string) => void;
}

const STATUS_COLORS: Record<string, string> = {
  active: "text-green-500",
  running: "text-green-500",
  idle: "text-text-muted",
  paused: "text-amber-500",
  error: "text-red-500",
  terminated: "text-text-muted",
  pending_approval: "text-amber-500",
};

export function MobileAgentSheet({
  open,
  onClose,
  selectedAgentId,
  onSelect,
}: MobileAgentSheetProps) {
  const { tenantId } = useTenant();
  const { data: agents = [] } = useAgentsByCompany(tenantId);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  const topLevelAgents = agents.filter((a) => !a.parentAgentId);
  const subAgentsByParent = new Map<string, typeof agents>();
  for (const a of agents) {
    if (a.parentAgentId) {
      if (!subAgentsByParent.has(a.parentAgentId)) subAgentsByParent.set(a.parentAgentId, []);
      subAgentsByParent.get(a.parentAgentId)!.push(a);
    }
  }

  // Group by department
  const byDept = new Map<string, typeof topLevelAgents>();
  for (const a of topLevelAgents) {
    const dept = a.department || "Other";
    if (!byDept.has(dept)) byDept.set(dept, []);
    byDept.get(dept)!.push(a);
  }

  const handleSelect = (agentId: string) => {
    onSelect(agentId);
    navigate(`/tenants/${tenantId}/workspace?agentId=${agentId}`);
    onClose();
  };

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/50 sm:hidden"
        onClick={onClose}
      />
      <div className="glass-raised fixed inset-x-0 bottom-0 z-50 sm:hidden max-h-[85vh] flex flex-col rounded-t-[28px] border-b-0 animate-in slide-in-from-bottom">
        <div className="mg-grabber" aria-hidden="true" />
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-border shrink-0">
          <div className="flex items-center gap-2">
            <Bot size={16} className="text-accent" />
            <span className="text-sm font-semibold text-text-primary">
              Select Agent ({agents.length})
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-surface-hover text-text-muted"
          >
            <X size={18} />
          </button>
        </div>

        {/* Agent list */}
        <div className="flex-1 overflow-y-auto pb-[env(safe-area-inset-bottom)]">
          {topLevelAgents.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-2">
              <Bot size={32} className="text-text-muted" />
              <p className="text-sm text-text-muted">No agents yet</p>
            </div>
          ) : (
            Array.from(byDept.entries())
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([dept, deptAgents]) => (
                <div key={dept} className="py-1">
                  <div className="px-4 py-1.5 text-[10px] uppercase tracking-wide font-semibold text-text-muted bg-surface/50">
                    {dept}
                  </div>
                  {deptAgents.map((agent) => {
                    const isSelected = agent.id === selectedAgentId;
                    const statusColor = STATUS_COLORS[agent.status] || "text-text-muted";
                    const subs = subAgentsByParent.get(agent.id) || [];
                    return (
                      <div key={agent.id}>
                        <button
                          onClick={() => handleSelect(agent.id)}
                          className={cn(
                            "flex w-full items-center gap-3 px-4 py-3 text-left transition-colors",
                            isSelected ? "bg-accent/10" : "hover:bg-surface-hover",
                          )}
                        >
                          <AgentChatAvatar avatarUrl={agent.avatarUrl} name={agent.name} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className={cn(
                                "text-sm font-medium truncate",
                                isSelected ? "text-accent" : "text-text-primary",
                              )}>
                                {agent.name}
                              </span>
                              <Circle size={7} className={cn("shrink-0 fill-current", statusColor)} />
                            </div>
                            {agentRoleLabel(agent) && (
                              <p className="text-[11px] text-text-muted truncate">{agentRoleLabel(agent)}</p>
                            )}
                          </div>
                        </button>
                        {subs.map((sub) => {
                          const isSubSelected = sub.id === selectedAgentId;
                          const subStatusColor = STATUS_COLORS[sub.status] || "text-text-muted";
                          return (
                            <button
                              key={sub.id}
                              onClick={() => handleSelect(sub.id)}
                              className={cn(
                                "flex w-full items-center gap-3 pl-10 pr-4 py-2.5 text-left transition-colors",
                                isSubSelected ? "bg-accent/10" : "hover:bg-surface-hover",
                              )}
                            >
                              <AgentChatAvatar avatarUrl={sub.avatarUrl} name={sub.name} size="sm" />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <span className={cn(
                                    "text-xs font-medium truncate",
                                    isSubSelected ? "text-accent" : "text-text-secondary",
                                  )}>
                                    {sub.name}
                                  </span>
                                  <Circle size={5} className={cn("shrink-0 fill-current", subStatusColor)} />
                                </div>
                                {agentRoleLabel(sub) && (
                                  <p className="text-[10px] text-text-muted truncate">{agentRoleLabel(sub)}</p>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              ))
          )}
        </div>
      </div>
    </>
  );
}
